import { useEffect, useState } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/joy";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Breadcrumb } from "../../../../components";
import { Alert, Button } from "../../../../shared";
import { useApiResponseParser, useAuth, useAuthorization } from "../../../../hooks";
import { useRoles, useUser, useUserRoles } from "../../../../hooks/query";
import { PERMISSIONS } from "../../../../constants/permissions";
import { RoleSelectionList } from "../";

interface UserRecord {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
}

interface UserRoleRow {
    Id: string;
    RoleId: string;
    RoleName: string;
}

export default function EditUser() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { parseErrorResponseData } = useApiResponseParser();
    const { selectedOrganization } = useAuth();
    const { can } = useAuthorization();
    const canManage = can(PERMISSIONS.USERS_MANAGE);

    const { useGetUserById } = useUser();
    const { useList: useListRoles } = useRoles();
    const { useListByUser, useSync } = useUserRoles();

    const getUserSvc = useGetUserById();
    const listUserRolesSvc = useListByUser();
    const syncRolesSvc = useSync();
    const { data: allRoles = [], isLoading: rolesLoading } = useListRoles();

    const [user, setUser] = useState<UserRecord | null>(null);
    const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);

    useEffect(() => {
        if (!id || !selectedOrganization?.Id) return;

        const load = async () => {
            setIsLoadingUser(true);
            setLoadError(null);
            try {
                const [userData, userRoles] = await Promise.all([
                    getUserSvc.mutateAsync({
                        id,
                        OrganizationId: selectedOrganization.Id,
                    }),
                    listUserRolesSvc.mutateAsync({
                        userId: id,
                        OrganizationId: selectedOrganization.Id,
                    }),
                ]);
                setUser(userData as UserRecord);
                setSelectedRoleIds((userRoles as UserRoleRow[]).map((row) => row.RoleId));
            } catch (error) {
                setLoadError(parseErrorResponseData(error) || "Failed to load user.");
            } finally {
                setIsLoadingUser(false);
            }
        };

        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, selectedOrganization?.Id]);

    const handleSave = async () => {
        if (!canManage || !id || !selectedOrganization?.Id) return;
        try {
            await syncRolesSvc.mutateAsync({
                UserId: id,
                RoleIds: selectedRoleIds,
                OrganizationId: selectedOrganization.Id,
            });
            navigate("/admin/users");
        } catch (error) {
            console.error("Failed to save user roles:", error);
        }
    };

    if (!selectedOrganization?.Id) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Select an organization to manage users." />
            </Box>
        );
    }

    if (isLoadingUser || rolesLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (loadError || !user) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage={loadError || "User not found"} />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider />

            <Box p={3} pb={0} maxWidth={700}>
                <Typography level="title-lg">{canManage ? "Edit User" : "User Details"}</Typography>
                <Typography level="body-sm">
                    {canManage ? "Update roles for this user." : "View roles for this user."}
                </Typography>
            </Box>

            <Box ml={3} mt={2} maxWidth={700} pb={4}>
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box>
                        <Typography
                            level="body-xs"
                            sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                            User
                        </Typography>
                        <Typography level="body-md" sx={{ mt: 0.25, fontWeight: 600 }}>
                            {user.FirstName} {user.LastName}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                            {user.Email}
                        </Typography>
                    </Box>

                    <RoleSelectionList
                        roles={allRoles}
                        selectedRoleIds={selectedRoleIds}
                        onChange={setSelectedRoleIds}
                        loading={rolesLoading}
                        disabled={!canManage}
                    />

                    <Box mt={1} display="flex" alignItems="center" gap={1.5}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<ArrowLeft size={18} />}
                            onClick={() => navigate("/admin/users")}
                            disabled={syncRolesSvc.isPending}
                            sx={{ borderRadius: "4px" }}
                        >
                            {canManage ? "Cancel" : "Back"}
                        </Button>
                        {canManage ? (
                            <Button
                                variant="soft"
                                color="primary"
                                startDecorator={<Shield size={18} />}
                                loading={syncRolesSvc.isPending}
                                onClick={handleSave}
                                disabled={syncRolesSvc.isPending}
                                sx={{ borderRadius: "4px" }}
                            >
                                Save roles
                            </Button>
                        ) : null}
                    </Box>

                    {canManage && syncRolesSvc.isError ? (
                        <Alert errorMessage={parseErrorResponseData(syncRolesSvc.error)} />
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
